import { useEffect, useMemo } from 'react';
import { Form } from 'antd';
import { useQueryClient } from '@tanstack/react-query';
import { useDataExtractionReport } from '@/context/DataExtractionReport';
import {
	useUpdateDataExtraction,
	useCreateDataExtractionReport,
	useDeleteDataExtractionReport,
	useGetDataExtractionSelectedReportsCount
} from '@/hooks';
import { QUERY_KEYS } from '@/utils/query-keys';
import { REPORT_TYPES } from '@/utils/constants';

const { useWatch } = Form;

interface CreatePayloadReturn {
	data_extraction_id: string;
	organization_id: string;
	company_id: string;
	reports: Array<{
		report_type: string;
		custom_report_id: string | null;
		bera_report_id: string | null;
		sera_report_id: string | null;
	}>;
}

export interface CustomReportData {
	id: string;
	name: string;
	acronym: string;
	description: string;
	total_reports: number;
	report_type?: string;
	formatted_name?: string;
}

export function useHandleReports(customReports: CustomReportData[] = [], totalReports?: number) {
	const selectedReportTypeId: string | undefined = useWatch(['reports', 'custom_report_type_id']);
	const {
		dataExtractionReport,
		isSelectAllMode,
		onSetIsSelectAllMode,
		onSetDataExtractionReport,
		onSetSelectedReportsCount
	} = useDataExtractionReport();
	const queryClient = useQueryClient();
	const { isLoading: isUpdatingReport, mutateAsync: updateDataExtraction } = useUpdateDataExtraction();
	const { isLoading: isCreatingReport, mutateAsync: createDataExtractionReport } = useCreateDataExtractionReport();
	const { isLoading: isDeletingReport, mutateAsync: deleteDataExtractionReport } = useDeleteDataExtractionReport();

	const { data: selectedCount } = useGetDataExtractionSelectedReportsCount({
		organization_id: dataExtractionReport?.organization_id || '',
		data_extraction_id: dataExtractionReport?.id
	});

	useEffect(() => {
		if (selectedCount?.count !== undefined) {
			onSetSelectedReportsCount(selectedCount.count);
		}
	}, [selectedCount?.count, onSetSelectedReportsCount]);

	useEffect(() => {
		if (totalReports !== undefined && selectedCount?.count !== undefined) {
			const isSelectAll = totalReports === selectedCount.count;
			if (isSelectAll !== isSelectAllMode) {
				onSetIsSelectAllMode(isSelectAll);
			}
		}
	}, [totalReports, selectedCount?.count, isSelectAllMode, onSetIsSelectAllMode]);

	const updateCustomReportContext = useMemo(() => {
		return (selectedReportId?: string) => {
			if (!dataExtractionReport) return;

			const reportData = selectedReportId ? customReports.find((r) => r.id === selectedReportId) : null;

			onSetDataExtractionReport({
				...dataExtractionReport,
				custom_report: reportData
					? {
							acronym: reportData.acronym,
							name: reportData.formatted_name!,
							report_type: reportData.report_type
					  }
					: undefined,
				custom_report_id: selectedReportId || null
			});
		};
	}, [customReports, dataExtractionReport, onSetDataExtractionReport]);

	async function clearAllReports(selectedReportTypeId?: string): Promise<void> {
		onSetIsSelectAllMode(false);

		const payload = {
			id: dataExtractionReport?.id!,
			organization_id: dataExtractionReport?.organization_id!,
			company_id: dataExtractionReport?.company_id!,
			custom_report_id: selectedReportTypeId || null,
			current_step_sequence: 1,
			data_extraction_reports: [],
			select_all_reports: false
		};

		await updateDataExtraction(payload);

		await queryClient.invalidateQueries([QUERY_KEYS.GET_CUSTOM_REPORT_BY_TYPE_LIST]);
	}

	async function handleSelectAll(isSelectAllMode: boolean): Promise<void> {
		onSetIsSelectAllMode(isSelectAllMode);

		const custom_report_id = selectedReportTypeId || dataExtractionReport?.custom_report_id || null;

		const commonPayload = {
			id: dataExtractionReport?.id!,
			organization_id: dataExtractionReport?.organization_id!,
			company_id: dataExtractionReport?.company_id!,
			custom_report_id,
			current_step_sequence: 1
		};

		const payload = isSelectAllMode
			? { ...commonPayload, select_all_reports: true }
			: { ...commonPayload, data_extraction_reports: [] };

		await updateDataExtraction(payload);
		await queryClient.invalidateQueries([QUERY_KEYS.GET_CUSTOM_REPORT_BY_TYPE_LIST]);
		await queryClient.invalidateQueries([QUERY_KEYS.GET_DATA_EXTRACTION_SELECTED_REPORTS_COUNT]);
	}

	function handleSelectAllClick(): void {
		handleSelectAll(true);
	}

	function handleDeselectAllClick(): void {
		handleSelectAll(false);
	}

	function createPayload(reportIds: string[], reportType?: string): CreatePayloadReturn {
		return {
			data_extraction_id: dataExtractionReport?.id!,
			organization_id: dataExtractionReport?.organization_id!,
			company_id: dataExtractionReport?.company_id!,
			reports: reportIds.map((reportId: string) => ({
				report_type: reportType || dataExtractionReport?.custom_report?.report_type || '',
				custom_report_id: reportType === REPORT_TYPES.CUSTOM ? reportId : null,
				bera_report_id: reportType === REPORT_TYPES.BERA ? reportId : null,
				sera_report_id: reportType === REPORT_TYPES.SERA ? reportId : null
			}))
		};
	}

	async function handleCreateReports(reportId: string, reportType?: string): Promise<void> {
		await createDataExtractionReport(createPayload([reportId], reportType));
	}

	async function handleDeleteReports(reportId: string, reportType?: string): Promise<void> {
		await deleteDataExtractionReport(createPayload([reportId], reportType));
	}

	async function handleCreateReportsBatch(reportIds: string[], reportType?: string): Promise<void> {
		await createDataExtractionReport(createPayload(reportIds, reportType));
	}

	async function handleDeleteReportsBatch(reportIds: string[], reportType?: string): Promise<void> {
		await deleteDataExtractionReport(createPayload(reportIds, reportType));
	}

	return {
		isSelectAllMode,
		selectedReportTypeId,
		selectedReportsCount: selectedCount?.count || 0,
		handleSelectAll,
		handleSelectAllClick,
		handleDeselectAllClick,
		handleCreateReports,
		handleDeleteReports,
		handleCreateReportsBatch,
		handleDeleteReportsBatch,
		createPayload,
		clearAllReports,
		updateCustomReportContext,
		isUpdatingReport,
		isCreatingReport,
		isDeletingReport
	};
}
